// React, Next
import { useState } from 'react'
import Image, { StaticImageData } from 'next/image'

// MUI
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import CodeIcon from '@mui/icons-material/Code'

// Style
import themeColors from 'assets/theme/colors'

/** How close to an edge the divider gets before that side's label fades out. */
const EDGE_MARGIN = 10

type Props = {
  before: StaticImageData
  after: StaticImageData
  beforeLabel: string
  afterLabel: string
  /** Short affordance shown under the image, e.g. "Drag to compare". */
  hint?: string
  /** Aspect ratio of the frame. Both photos are cropped to fill it. */
  ratio?: string
}

/**
 * Two photographs stacked in the same frame. The "before" image sits on top and
 * is clipped away from the left as the handle moves right, revealing the
 * "after" image underneath.
 *
 * The drag itself is a range input laid over the frame at zero opacity: it
 * gives pointer, touch and keyboard control for free, which a bare div with
 * pointer handlers would not.
 */
export default function BeforeAfterSlider(props: Props) {
  const [position, setPosition] = useState(50)

  const label = (p: {
    text: string
    side: 'left' | 'right'
    accent: boolean
    visible: boolean
  }) => (
    <Typography
      component="span"
      sx={{
        position: 'absolute',
        // Along the top edge, where nothing in the photos competes with them.
        top: '0.75rem',
        [p.side]: '0.75rem',
        zIndex: 2,
        fontSize: '0.7rem',
        fontWeight: 700,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        color: '#fff',
        background: p.accent
          ? themeColors.success.main
          : 'rgba(18, 17, 57, 0.78)',
        borderRadius: '4px',
        padding: '0.25rem 0.6rem',
        pointerEvents: 'none',
        // Fades as the divider nears, rather than being clipped by it.
        opacity: p.visible ? 1 : 0,
        transition: 'opacity 0.15s ease'
      }}
    >
      {p.text}
    </Typography>
  )

  return (
    <Box sx={{ width: '100%' }}>
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          aspectRatio: props.ratio ?? '4 / 3',
          borderRadius: '10px',
          overflow: 'hidden',
          userSelect: 'none'
        }}
      >
        {/* Underneath: the retrofitted machine */}
        <Image
          src={props.after}
          alt=""
          fill
          sizes="(max-width: 990px) 100vw, 50vw"
          style={{ objectFit: 'cover' }}
        />

        {/* On top: the original machine, clipped away from the left */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            clipPath: `inset(0 0 0 ${position}%)`
          }}
        >
          <Image
            src={props.before}
            alt=""
            fill
            sizes="(max-width: 990px) 100vw, 50vw"
            style={{ objectFit: 'cover' }}
          />
        </Box>

        {label({
          text: props.afterLabel,
          side: 'left',
          accent: true,
          visible: position > EDGE_MARGIN
        })}
        {label({
          text: props.beforeLabel,
          side: 'right',
          accent: false,
          visible: position < 100 - EDGE_MARGIN
        })}

        {/* Divider and grab handle */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            bottom: 0,
            left: `${position}%`,
            width: '3px',
            marginLeft: '-1.5px',
            background: '#fff',
            boxShadow: '0 0 6px rgba(0,0,0,0.35)',
            pointerEvents: 'none',
            zIndex: 2
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '2.4rem',
              height: '2.4rem',
              borderRadius: '50%',
              background: '#fff',
              boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <CodeIcon
              sx={{
                fontSize: '1.3rem',
                color: themeColors.kroemtech.dunkelblau
              }}
            />
          </Box>
        </Box>

        {/* The actual control - invisible, but keyboard and touch friendly */}
        <Box
          component="input"
          type="range"
          min={0}
          max={100}
          value={position}
          aria-label={`${props.beforeLabel} / ${props.afterLabel}`}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setPosition(Number(e.target.value))
          }
          sx={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            margin: 0,
            opacity: 0,
            cursor: 'ew-resize',
            zIndex: 3,
            appearance: 'none',
            '&::-webkit-slider-thumb': {
              appearance: 'none',
              width: '2.4rem',
              height: '2.4rem'
            }
          }}
        />
      </Box>

      {props.hint && (
        <Typography
          sx={{
            fontSize: '0.8rem',
            textAlign: 'center',
            color: themeColors.gray[500],
            marginTop: '0.75rem'
          }}
        >
          {props.hint}
        </Typography>
      )}
    </Box>
  )
}
